import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button } from 'antd';
import whitelabelConfig from "@whitelabel-config";

const IdleMonitor: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const { t } = useTranslation();
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const logoutTimerRef = useRef<NodeJS.Timeout | null>(null);
  const events = ['mousemove', 'click', 'keydown', 'wheel'];
  const shouldMonitorPage = () => {
    const path = window.location.pathname;
    return !(
      path === '/auth/2fa' ||
      path.startsWith('/s/') ||
      path === '/' ||
      path === '/auth/pii' ||
      path === '/memberlogin' ||
      path === '/language'
    );
  };

  const handleIdleEvent = () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => showIdleModal(), whitelabelConfig.uiSettings.logoutTimeout);
  };

  useEffect(() => {
    const handleRouteChange = () => {
      if (shouldMonitorPage()) {
        addEventListeners();
        startIdleTimer();
      } else {
        cleanup();
      }
    };

    if (shouldMonitorPage()) {
      addEventListeners();
      startIdleTimer();
    }

    window.addEventListener('popstate', handleRouteChange);
    return () => {
      cleanup();
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [window.location.pathname]);

  const addEventListeners = () => {
    events.forEach(event => window.addEventListener(event, handleIdleEvent));
  };

  const cleanup = () => {
    events.forEach(event => window.removeEventListener(event, handleIdleEvent));
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
  };

  const startIdleTimer = () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => showIdleModal(), whitelabelConfig.uiSettings.idleTimeout);
  };

  const showIdleModal = () => {
    setShowModal(true);
    logoutTimerRef.current = setTimeout(logOut, whitelabelConfig.uiSettings.logoutTimeout); // Logout after 10 seconds if no action is taken
  };

  const onExtend = () => {
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    setShowModal(false);
    startIdleTimer();
  };

  const logOut = () => {
    setShowModal(false);
    window.location.href = '/memberlogin';
  };

  return (
    <div>
      <Modal
        title={t('GLOBAL.SESSION_TIMEOUT')}
        visible={showModal}
        onCancel={onExtend}
        footer={null}
        destroyOnClose
      >
        <h2>{t('GLOBAL.SESSION_EXPIRATION')}</h2>
        <p>{t('GLOBAL.SESSION_EXPIRE', { MINUTE: process.env.REACT_APP_MINUTE })}</p>
        <div>
          <Button onClick={logOut} type="primary">{t('GLOBAL.LOGOUT')}</Button>
          <Button onClick={onExtend} style={{ marginLeft: 8 }}>{t('GLOBAL.EXTEND_SESSION')}</Button>
        </div>
      </Modal>
    </div>
  );
};

export default IdleMonitor;
